import React from 'react';
import { Trophy } from 'lucide-react';

/**
 * CryptoIcon Component
 * Displays cryptocurrency logos with fallback
 * Uses cryptocurrency-icons library for authentic brand logos
 */
const CryptoIcon = ({ symbol, size = 32, className = '' }) => {
  // Map common symbols to cryptocurrency-icons format
  const symbolMap = {
    'BTC': 'btc',
    'ETH': 'eth',
    'BNB': 'bnb',
    'SOL': 'sol',
    'XRP': 'xrp',
    'DOGE': 'doge',
    'ADA': 'ada',
    'AVAX': 'avax',
    'DOT': 'dot',
    'MATIC': 'matic',
    'LTC': 'ltc',
    'LINK': 'link',
    'UNI': 'uni',
    'ATOM': 'atom',
    'TRX': 'trx',
    'TON': 'ton',
    'USDC': 'usdc',
    'USDT': 'usdt',
    'SHIB': 'shib',
    'APT': 'apt',
    'ARB': 'arb',
    'NEAR': 'near',
    'FTM': 'ftm',
    'ALGO': 'algo',
    'VET': 'vet',
    'ICP': 'icp',
    'FIL': 'fil',
    'AAVE': 'aave',
    'ETC': 'etc',
    'XLM': 'xlm',
    'HBAR': 'hbar',
    'MANA': 'mana',
    'SAND': 'sand',
    'AXS': 'axs',
    'THETA': 'theta',
    'EOS': 'eos',
    'GOLD': 'gold',
  };

  // Color gradients for each coin
  const colorMap = {
    'BTC': 'from-orange-400 to-orange-600',
    'ETH': 'from-purple-400 to-purple-600',
    'BNB': 'from-yellow-400 to-yellow-600',
    'SOL': 'from-blue-400 to-blue-600',
    'XRP': 'from-cyan-400 to-cyan-600',
    'ADA': 'from-blue-500 to-blue-700',
    'DOGE': 'from-yellow-300 to-yellow-500',
    'AVAX': 'from-red-400 to-red-600',
    'DOT': 'from-pink-400 to-pink-600',
    'MATIC': 'from-purple-500 to-purple-700',
    'LTC': 'from-gray-400 to-gray-600',
    'LINK': 'from-blue-500 to-blue-700',
    'UNI': 'from-pink-500 to-pink-700',
    'ATOM': 'from-indigo-400 to-indigo-600',
    'XLM': 'from-cyan-500 to-cyan-700',
    'ALGO': 'from-gray-700 to-gray-900',
    'FTM': 'from-blue-600 to-blue-800',
    'NEAR': 'from-green-400 to-green-600',
    'AAVE': 'from-purple-600 to-purple-800',
    'USDT': 'from-green-400 to-green-600',
    'USDC': 'from-blue-400 to-blue-600',
    'GOLD': 'from-yellow-400 via-yellow-500 to-yellow-600',
  };

  const upperSymbol = symbol.toUpperCase();
  
  // Special rendering for GOLD
  if (upperSymbol === 'GOLD') {
    return (
      <div 
        className={`flex items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 shadow-lg shadow-yellow-500/50 ${className}`}
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <Trophy className="text-white" style={{ width: `${size * 0.6}px`, height: `${size * 0.6}px` }} />
      </div>
    );
  }

  const iconSymbol = symbolMap[upperSymbol] || symbol.toLowerCase();
  const gradient = colorMap[upperSymbol] || 'from-cyan-400 to-cyan-600';
  
  // Build the icon URL from cryptocurrency-icons CDN
  const iconUrl = `https://raw.githubusercontent.com/spothq/cryptocurrency-icons/master/128/color/${iconSymbol}.png`;

  return (
    <div className="relative" style={{ width: `${size}px`, height: `${size}px` }}>
      <img
        src={iconUrl}
        alt={`${symbol} logo`}
        style={{ width: `${size}px`, height: `${size}px` }}
        className={`rounded-full shadow-lg ${className}`}
        onError={(e) => {
          // Fallback: show a gradient circle with symbol
          e.target.style.display = 'none';
          const fallback = e.target.nextSibling;
          if (fallback) fallback.style.display = 'flex';
        }}
      />
      <div 
        className={`hidden items-center justify-center rounded-full bg-gradient-to-br ${gradient} shadow-lg ${className}`}
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <span className="text-white font-bold" style={{ fontSize: `${size * 0.4}px` }}>
          {symbol.slice(0, 2).toUpperCase()}
        </span>
      </div>
    </div>
  );
};

export default CryptoIcon;
